import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, LinkButton, Table, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { ExportCsvButton } from "@/components/admin/ExportCsvButton";
import { ImportCsvButton } from "@/components/admin/ImportCsvButton";
import { ClassificationBadge } from "@/components/public/ClassificationBadge";

const CLASSIFICATION_RANK = { GREEN: 0, YELLOW: 1, RED: 2 };

export default function AdminMaterialsList() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["admin-materials"], queryFn: async () => (await api.get("/admin/materials")).data });
  const { sort, toggleSort, sortRows } = useTableSort();

  // Default order (when no column sort active): GREEN → YELLOW → RED then alpha.
  const defaultSorted = useMemo(
    () =>
      [...data].sort((a, b) => {
        const ra = CLASSIFICATION_RANK[a.classification] ?? 99;
        const rb = CLASSIFICATION_RANK[b.classification] ?? 99;
        if (ra !== rb) return ra - rb;
        return (a.name || "").localeCompare(b.name || "");
      }),
    [data]
  );

  const sorted = sortRows(defaultSorted, (m, key) => {
    if (key === "classification") return CLASSIFICATION_RANK[m.classification] ?? 99;
    if (key === "is_public") return m.is_public ? 1 : 0;
    return m[key] ?? null;
  });

  return (
    <div data-testid="admin-materials-list">
      <PageHeader title="Materials" subtitle={`${data.length} materials`} actions={<div className="flex gap-2"><ImportCsvButton endpoint="/admin/materials/import.csv" testid="import-materials-csv" onSuccess={() => qc.invalidateQueries({ queryKey: ["admin-materials"] })} /><ExportCsvButton endpoint="/admin/materials/export.csv" filename="materials.csv" testid="export-materials-csv" /><LinkButton to="/admin/materials/new">+ New material</LinkButton></div>} />
      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Classification" sortKey="classification" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Summary" sortKey="short_summary" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Public" sortKey="is_public" sort={sort} onSort={toggleSort} />
              <SortableHeader label="" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((m) => (
              <tr key={m.id} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]">
                <td className="px-3 py-3 font-medium">{m.name}</td>
                <td className="px-3 py-3"><ClassificationBadge classification={m.classification} /></td>
                <td className="px-3 py-3 text-xs text-[#6C757D] max-w-md truncate">{m.short_summary}</td>
                <td className="px-3 py-3">{m.is_public ? "Yes" : "No"}</td>
                <td className="px-3 py-3"><Link to={`/admin/materials/${m.id}`} className="underline text-sm">Edit</Link></td>
              </tr>
            ))}
            {!isLoading && sorted.length === 0 && <tr><td colSpan={5} className="px-3 py-12 text-center text-[#6C757D]">No materials yet.</td></tr>}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

